<script setup lang="ts">
import { computed } from 'vue';
import { characterTemplatesService } from '@/services/CharacterTemplatesService.ts';

interface CharacterTemplate {
  id?: string;
  name: string;
  description?: string;
  behavior?: string;
  avatar?: string;
  category?: string;
  level?: number;
  race?: string;
  class?: string;
}

interface Props {
  templateId: string;
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
});

// Get template data from service using the ID
const template = computed(() => {
  return characterTemplatesService.getTemplateById(props.templateId);
});

// Check if template is loaded
const isTemplateLoaded = computed(() => {
  return template.value !== null && template.value !== undefined;
});

const emit = defineEmits<{
  click: [];
  copyId: [templateId: string];
  filterByCategory: [template: CharacterTemplate];
  filterByBehavior: [template: CharacterTemplate];
}>();

// Helper methods
const getTemplateInitials = (name: string) => {
  if (!name) return '??';
  return name.substring(0, 2).toUpperCase();
};

const formatBehavior = (behavior?: string) => {
  if (!behavior) return '';

  // Convert camelCase or snake_case to Title Case with spaces
  return behavior
    // Insert a space before all uppercase letters
    .replace(/([A-Z])/g, ' $1')
    // Replace underscores with spaces
    .replace(/_/g, ' ')
    // Capitalize first letter
    .replace(/^./, str => str.toUpperCase())
    // Trim any extra spaces
    .trim();
};

const handleClick = () => {
  emit('click');
};

const handleCopyId = (event: Event) => {
  event.stopPropagation();
  if (props.template.id) {
    emit('copyId', props.template.id);
  }
};

const handleFilterByCategory = (event: Event) => {
  event.stopPropagation();
  emit('filterByCategory', props.template);
};

const handleFilterByBehavior = (event: Event) => {
  event.stopPropagation();
  emit('filterByBehavior', props.template);
};

// Handle image load errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  const parent = img.parentNode as HTMLElement;

  // Create fancy placeholder
  const placeholder = document.createElement('div');
  placeholder.className = 'template-placeholder fancy-placeholder';

  // Add initials
  const initials = document.createElement('span');
  initials.className = 'initials';
  initials.textContent = getTemplateInitials(template.value?.name || '');
  placeholder.appendChild(initials);

  // Replace the image with the placeholder
  parent.replaceChild(placeholder, img);
};
</script>

<template>
  <div
    class="gm-panel template-card"
    :class="{ 'selected': selected, 'loading': !isTemplateLoaded }"
    @click="handleClick"
  >
    <!-- Loading state -->
    <div v-if="!isTemplateLoaded" class="template-loading">
      <div class="loading-placeholder">
        <span class="dx-loader" aria-hidden="true"></span>
        <div class="loading-text">Loading...</div>
      </div>
    </div>

    <!-- Template content -->
    <template v-else>
      <img
          v-if="template.avatar"
          :src="template.avatar"
          :alt="template.name"
          class="template-image"
          @error="handleImageError"
      />
      <div class="template-info">
        <div class="template-name" :title="template.name">
          {{ template.name }}
        </div>

        <div v-if="template.behavior" class="template-behavior">
          {{ formatBehavior(template.behavior) }}
        </div>

        <div v-if="template.category" class="gm-chip template-category">
          {{ template.category }}
        </div>

        <div v-if="template.level" class="template-level">
          Level {{ template.level }}
        </div>

        <div class="template-details">
          <span v-if="template.race" class="gm-chip gm-chip--muted template-race">{{ template.race }}</span>
          <span v-if="template.class" class="gm-chip gm-chip--muted template-class">{{ template.class }}</span>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="template-actions">
        <button
          v-if="template.id"
          @click="handleCopyId"
          class="gm-btn gm-btn--sm action-btn"
          title="Copy Template ID"
        >
          📋
        </button>

        <button
          v-if="template.category"
          @click="handleFilterByCategory"
          class="gm-btn gm-btn--sm action-btn"
          title="Filter by Category"
        >
          🏷️
        </button>

        <button
          v-if="template.behavior"
          @click="handleFilterByBehavior"
          class="gm-btn gm-btn--sm action-btn"
          title="Filter by Behavior"
        >
          🎭
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.template-card {
  display: flex;
  flex-direction: column;
  padding: 0.7rem;
  cursor: pointer;
  position: relative;
  min-height: 140px;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.template-card:hover {
  border-color: var(--gm-border);
  background: var(--gm-accent-soft);
}

.template-card.selected {
  border-color: var(--gm-border-hi);
}

.template-card.loading {
  opacity: 0.7;
  cursor: default;
}

.template-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 140px;
}

.loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.loading-text {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
}

.template-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 60%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  mask: linear-gradient(to right, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 0) 100%);
  z-index: -1;
  opacity: 0.9;
}

.template-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-radius: 50%;
}

.fancy-placeholder {
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border-dim);
  overflow: hidden;
  position: relative;
}

.fancy-placeholder .initials {
  position: relative;
  z-index: 2;
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--gm-accent);
}

.template-info {
  flex: 1;
  text-align: right;
}

.template-name {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gm-text);
}

.template-behavior {
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  margin-bottom: 0.25rem;
  font-style: italic;
}

.template-category {
  margin-bottom: 0.25rem;
}

.template-level {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
  margin-bottom: 0.25rem;
}

.template-details {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.65rem;
}

.template-actions {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.template-card:hover .template-actions {
  opacity: 1;
}

.action-btn {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
}

/* Responsive design */
@media (max-width: 640px) {
  .template-card {
    min-height: 120px;
    padding: 0.5rem;
  }

  .template-avatar {
    width: 50px;
    height: 50px;
  }

  .template-name {
    font-size: 0.8rem;
  }

  .template-behavior,
  .template-category,
  .template-level {
    font-size: 0.65rem;
  }

  .template-details {
    font-size: 0.6rem;
  }
}
</style>